import { useMutation, useQuery } from '@tanstack/react-query';

import { Section } from '../common/contracts';
import { EQueryKeys, ESectionOfferType } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  ApiRoutes,
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';

export default function useSections(
  currentPublisherId?: string,
  sectionOfferType?: ESectionOfferType
) {
  const axios = useAxios();

  const getSections = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.SECTIONS],
    queryFn: async () => {
      return await axios.get(getApiRouteOrMock(EApiRoutes.SECTIONS));
    },
    enabled: !!currentPublisherId
  });

  const getSectionsByOfferType = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.SECTIONS_BY_OFFER_TYPE, sectionOfferType],
    queryFn: async () => {
      const sections = await axios.get(
        `${ApiRoutes.SECTIONS}?offer_type=${sectionOfferType}`,
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return sections;
    },
    enabled: !!sectionOfferType && !!currentPublisherId
  } as const);

  const addSections = useMutation(async (data: Partial<Section>) => {
    return await axios.post(getApiRouteOrMock(EApiRoutes.SECTIONS), data);
  }, {});

  const updateSections = useMutation(async (data: Partial<Section>) => {
    return await axios.put(getApiRouteOrMock(EApiRoutes.SECTIONS), data);
  }, {});

  const deleteSections = useMutation(async (data: Partial<Section>) => {
    return await axios.del(getApiRouteOrMock(EApiRoutes.SECTIONS), data);
  }, {});

  const upsertSections = useMutation(async (data: any) => {
    return await axios.post(
      getApiRouteOrMock(EApiRoutes.UPSERT_SECTIONS),
      data
    );
  }, {});

  return {
    getSections,
    getSectionsByOfferType,
    addSections,
    updateSections,
    upsertSections,
    deleteSections
  };
}
